import imageCompression from 'browser-image-compression';
import * as Sentry from '@sentry/react';

/**
 * Compresses an image file if it exceeds the specified size limit
 * @param {File} file - The image file to compress
 * @param {Object} options - Compression options
 * @param {number} options.maxSizeMB - Maximum size in MB (default: 4)
 * @param {number} options.maxWidthOrHeight - Maximum dimension (default: 2048)
 * @param {number} options.initialQuality - Initial quality (default: 0.8)
 * @returns {Promise<File>} - The compressed file or original file if compression isn't needed
 */

type CompressionOptions = {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  initialQuality?: number;
};


export const compressImageIfNeeded = async (file, options: CompressionOptions = {}):Promise<File> => {
  // Check if it's an image file
  if (!file.type.startsWith('image/')) {
    return file;
  }

  const {
    maxSizeMB = 4,
    maxWidthOrHeight = 2048,
    initialQuality = 0.8,
  } = options;

  try {
    const compressionOptions = {
      maxSizeMB,
      maxWidthOrHeight,
      useWebWorker: true,
      fileType: file.type,
      initialQuality,
    };

    const compressedFile = await imageCompression(file, compressionOptions);
    
    // Create a new File object with the original name and properties
    const newFile = new File([compressedFile], file.name, {
      type: compressedFile.type,
      lastModified: file.lastModified,
    });

    console.log(`Image compressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(newFile.size / 1024 / 1024).toFixed(2)}MB`);

    return newFile;
  } catch (error) {
    console.error('Image compression failed:', error);

    // Report compression failures to Sentry
    Sentry.captureException(error, {
      tags: {
        image_compression_error: true,
      },
      extra: {
        fileSize: file.size,
        fileType: file.type,
        maxSizeMB,
        maxWidthOrHeight,
        initialQuality,
      },
    });

    // Return original file if compression fails
    return file;
  }
};

/**
 * Gets a human-readable file size string
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size string
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
